import { OfflineErrorCode, OfflineScene, OfflineViewState, View } from "@novorender/api";
import pMap from "p-map";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { dataApi } from "apis/dataV1";
import { bookmarkStatePath, useLazyGetBookmarkCatalogQuery, useLazyGetGroupIdsQuery } from "apis/dataV2/dataV2Api";
import { dataV2Headers } from "apis/dataV2/utils";
import { createBimSceneFileSignedDownloadUrl } from "apis/procore/bimSceneFileSignedDownloadUrl";
import { buildModelRevisionsArgs, selectSceneRevision } from "apis/procore/modelRevisions";
import {
    useLazyGetMiniMapDataQuery,
    useLazyGetModelRevisionsQuery,
    useLazyGetModelsQuery,
} from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { StorageKey } from "config/storage";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { pickDataAccess } from "dataAccess/pickDataAccess";
import {
    type MinimapFullSyncScene,
    type MinimapPruneScene,
    pruneStaleMinimapFiles,
    registerMinimapOfflineStore,
    syncMinimapFiles,
    unregisterMinimapOfflineStore,
} from "features/miniMap/tiledDrawing";
import { useAbortController } from "hooks/useAbortController";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import {
    selectBackendProvider,
    selectConfig,
    selectIncrementalCachingDisabled,
    selectIsProcoreDesign,
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
    selectProjectV2Info,
} from "slices/explorer";
import { telemetry } from "telemetry";
import type { OfflineDownloadErrorCode, OfflineDownloadStatus, OfflineDownloadStatusPayload } from "types/events";
import { isPhone, isTablet } from "utils/misc";
import { getFromStorage } from "utils/storage";

import { offlineActions, selectIncrementalStorageLimit, selectOfflineAction } from "./offlineSlice";
import { useOfflineSceneDirName, useOfflineSceneKey } from "./useOfflineSceneKey";

type OfflineSceneMeta = {
    id: string;
    name: string;
    lastSynced: string;
    viewerScenes: { name: string; id: string; lastSynced: string }[];
};

export const GB = 1000 * 1000 * 1000;
const DEFAULT_INCREMENTAL_STORAGE_LIMIT = isPhone ? 5 * GB : isTablet ? 10 * GB : 50 * GB;
const MAX_BOOKMARK_WARM_CONCURRENCY = 5;

/** Keyed by {@link OfflineScene.id} — the name of the directory holding the scene's files. */
const offlineSceneStorageKey = (sceneDirName: string) => `offline_scene/${sceneDirName}`;
const storage = {
    get: (sceneDirName: string): OfflineSceneMeta | undefined => {
        const stored = localStorage.getItem(offlineSceneStorageKey(sceneDirName));

        if (!stored) {
            return;
        }

        try {
            const scene = JSON.parse(stored) as OfflineSceneMeta;
            return scene;
        } catch (e) {
            console.warn(e);
            telemetry.trackCaughtException({
                source: "offline.useHandleOffline.getScene",
                category: "data",
                error: e,
            });
            localStorage.removeItem(offlineSceneStorageKey(sceneDirName));
        }
    },
    set: (scene: OfflineSceneMeta): void => {
        localStorage.setItem(offlineSceneStorageKey(scene.id), JSON.stringify(scene));
    },
    remove: (sceneDirName: string): void => {
        localStorage.removeItem(offlineSceneStorageKey(sceneDirName));
    },
};

export function getOfflineDownloadErrorMessage(
    errorId: OfflineErrorCode | undefined,
    translate: (key: "notEnoughSpaceError" | "networkDownloadError" | "genericDownloadError") => string,
): string {
    switch (errorId) {
        case OfflineErrorCode.quotaExceeded:
            return translate("notEnoughSpaceError");
        case OfflineErrorCode.offline:
            return translate("networkDownloadError");
        default:
            return translate("genericDownloadError");
    }
}

/**
 * The SDK statuses forwarded to the host as they arrive.
 *
 * Failures are absent on purpose: the SDK pairs every failing status with an `error` callback, so
 * they reach the host from there as one `"error"` event that carries an error code. `"deleting"` is
 * absent too — the SDK uses it for both a scene delete and stale-file pruning, so the delete action
 * reports its own. Statuses a future SDK adds are dropped rather than forwarded unrecognized.
 */
const HOST_REPORTED_SDK_STATUSES = new Set<string>([
    "scanning",
    "synchronizing",
    "synchronized",
    "aborted",
] satisfies OfflineDownloadStatus[]);

function isHostReportedStatus(status: string): status is OfflineDownloadStatus {
    return HOST_REPORTED_SDK_STATUSES.has(status);
}

/** Locale-independent counterpart of {@link getOfflineDownloadErrorMessage}, for the host. */
export function getOfflineDownloadErrorCode(errorId: OfflineErrorCode | undefined): OfflineDownloadErrorCode {
    switch (errorId) {
        case OfflineErrorCode.quotaExceeded:
            return "quotaExceeded";
        case OfflineErrorCode.offline:
            return "offline";
        case OfflineErrorCode.downloadFailed:
            return "downloadFailed";
        default:
            return "unknown";
    }
}

export function useHandleOffline() {
    const viewerSceneId = useSceneId();
    // What identifies the scene's offline files, as against the id it is loaded by — on the
    // Procore backend those differ once a version or a slice is in play. The SDK takes the
    // key; the record in storage and in the store goes under the name derived from it.
    const sceneKey = useOfflineSceneKey();
    const sceneDirName = useOfflineSceneDirName();
    const {
        state: { offlineWorkerState, view },
    } = useExplorerGlobals();
    const user = useAppSelector(selectUser);

    const action = useAppSelector(selectOfflineAction);
    const [abortController, abort] = useAbortController();
    const [_deleteStaleFilesAbortController, abortDeleteStaleFiles] = useAbortController();
    const { createLogger, completeFullSync, minimapPhaseActiveRef } = useCreateLogger();
    const emitDownloadStatus = useEmitDownloadStatus();
    const dispatch = useAppDispatch();
    const [getGroupIds] = useLazyGetGroupIdsQuery();
    const warmBookmarks = useWarmBookmarks();
    const projectId = useAppSelector(selectProjectV2Info)?.id;
    const incrementalStorageLimit = useAppSelector(selectIncrementalStorageLimit);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const incrementalCachingDisabled = useAppSelector(selectIncrementalCachingDisabled);
    const minimapTilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    const getProcoreModelTitle = useGetProcoreModelTitle();
    const syncMinimapFilesForScene = useSyncMinimapFiles();
    const getMinimapCurrentFileInstanceIds = useMinimapCurrentFileInstanceIds();
    const replaceDataAccessWithoutOffline = useReplaceDataAccessWithoutOffline();

    useEffect(
        function initOfflineScenes() {
            if (!offlineWorkerState || !view) {
                return;
            }
            let mounted = true;

            let initIncrementalStorageLimit = Number(getFromStorage(StorageKey.IncrementalOfflineLimit));
            if (!initIncrementalStorageLimit) {
                initIncrementalStorageLimit = DEFAULT_INCREMENTAL_STORAGE_LIMIT;
            }
            dispatch(offlineActions.setIncrementalStorageLimit(initIncrementalStorageLimit));

            offlineWorkerState.onIncrementalSizeChange = (size) => {
                dispatch(offlineActions.setIncrementalStorageSize(size));
            };

            offlineWorkerState.onIncrementalSceneRemoved = (removedSceneDirName) => {
                storage.remove(removedSceneDirName);
                dispatch(offlineActions.removeScene(removedSceneDirName));
            };

            // initEvictionLoop can potentially hang forever if another tab is
            // managing eviction right now and holding the lock
            offlineWorkerState
                .initEvictionLoop()
                .then(() => {
                    if (!mounted) {
                        return;
                    }
                    const thirtyDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 30;
                    offlineWorkerState.removeStaleIncrementalScenes(thirtyDaysAgo, [sceneKey]);
                })
                .catch((e) => {
                    console.error("Error initializing eviction loop:", e);
                    telemetry.trackCaughtException({
                        source: "offline.useHandleOffline.initEvictionLoop",
                        category: "data",
                        error: e,
                        severity: "error",
                    });
                });

            for (const scene of offlineWorkerState.iterateScenes()) {
                const meta = storage.get(scene.id);

                if (!meta) {
                    continue;
                }

                const isIncremental = scene.manifest.numFiles === 0;

                dispatch(
                    offlineActions.addScene({
                        id: scene.id,
                        name: meta.name,
                        status: isIncremental ? "incremental" : "synchronized",
                        lastSync: meta.lastSynced,
                        progress: "",
                        scanProgress: "",
                        size: scene.manifest.totalByteSize,
                        viewerScenes: meta.viewerScenes,
                    }),
                );

                // TODO(NSO-386): Uncomment when NSO-386 is fixed (restore incremental autoclean + `deleteStaleFiles` abort on scene delete).
                // if (isIncremental && scene.id === sceneDirName) {
                //     // Clean up stale files for the open scene in incremental mode
                //     console.log("Delete stale offline files");
                //     scene
                //         .deleteStaleFiles(getSceneIndexUrl(view), deleteStaleFilesAbortController.current.signal)
                //         .then((result) => {
                //             console.log("Stale files deleted", result);
                //         })
                //         .catch((error) => {
                //             console.error("Error deleting stale files:", error);
                //             telemetry.trackCaughtException({
                //                 source: "offline.useHandleOffline.deleteStaleFiles",
                //                 category: "data",
                //                 error,
                //                 severity: "error",
                //             });
                //         });
                // }

                scene.logger = createLogger(scene, isIncremental ? "incremental" : "fullSync");
            }

            // Procore auto-enables incremental on first load (web + mobile),
            // unless the iOS host kill switch (incrementalCachingDisabled) is on.
            // Standalone handles this via IncrementalLoadingPopup. Procore mobile
            // additionally exposes Download for offline (fullSync) in Settings.
            if (isProcoreDesign && !incrementalCachingDisabled && !offlineWorkerState.getScene(sceneKey)) {
                dispatch(offlineActions.setAction({ action: "incrementalSync" }));
            }

            return () => {
                mounted = false;
                if (offlineWorkerState) {
                    offlineWorkerState.onIncrementalSizeChange = undefined;
                    offlineWorkerState.onIncrementalSceneRemoved = undefined;
                }
            };
        },
        [offlineWorkerState, dispatch, sceneKey, createLogger, view, isProcoreDesign, incrementalCachingDisabled],
    );

    useEffect(() => {
        if (!offlineWorkerState || incrementalStorageLimit === null) {
            return;
        }
        offlineWorkerState.setIncrementalStorageLimit(incrementalStorageLimit);
    }, [offlineWorkerState, dispatch, incrementalStorageLimit]);

    // Arm the tiled minimap route's OPFS incremental caching (RFD00000145).
    // Gated on `isProcoreDesign` (web + mobile), NOT `isProcoreMobileDesign`:
    // deliberately kept platform-neutral, matching geometry's own incremental
    // caching, even though the tiled route itself is currently mobile-only
    // (BM-13878) and so this is transitively inert on web today. `readEnabled`
    // stays true regardless of the incremental kill switch so a fully-synced
    // scene still reads from OPFS. Looked up by the same versioned/sliced
    // `sceneKey` the geometry sync uses, so tiles land in -- and are read
    // from -- the directory holding that exact scene version's files.
    useEffect(() => {
        if (!offlineWorkerState || !minimapTilingEnabled) {
            return;
        }
        registerMinimapOfflineStore({
            getScene: () => offlineWorkerState.getScene(sceneKey),
            writeEnabled: isProcoreDesign && !incrementalCachingDisabled,
            readEnabled: true,
        });
        return unregisterMinimapOfflineStore;
    }, [offlineWorkerState, sceneKey, minimapTilingEnabled, isProcoreDesign, incrementalCachingDisabled]);

    // Best-effort stale-minimap-tree prune on scene load (BM-13752): covers a
    // scene that already had `minimap/{fid}` trees from a *previous* session
    // (incrementally cached, or fully synced) sitting on tiles for a drawing
    // that's since been re-mapped or removed from the scene -- the full-sync
    // phase below also prunes, but only after the next full sync actually
    // runs. Only fires once per scene directory, and only when the scene is
    // already armed (`getScene` is a synchronous lookup, not a promise -- if
    // it's not there yet, this session is the one arming it for the first
    // time via the "incrementalSync" action below, which prunes once it does).
    const minimapPruneAttemptedForSceneDirName = useRef<string>();
    useEffect(() => {
        if (!offlineWorkerState || !minimapTilingEnabled || !isProcoreDesign) {
            return;
        }
        if (minimapPruneAttemptedForSceneDirName.current === sceneDirName) {
            return;
        }
        const scene = offlineWorkerState.getScene(sceneKey);
        if (!scene) {
            return;
        }

        const abortController = new AbortController();
        (async () => {
            const currentFileInstanceIds = await getMinimapCurrentFileInstanceIds(viewerSceneId);
            await pruneStaleMinimapFiles({
                scene: scene as MinimapPruneScene,
                currentFileInstanceIds,
                abortSignal: abortController.signal,
            });
            // Only latch once this ran with a determinate drawing set. An abort
            // (cleanup firing mid-flight -- a dependency changing, or React
            // Strict Mode's mount-cleanup-remount in dev) makes
            // `pruneStaleMinimapFiles` throw before this line runs at all, and
            // `undefined` (offline, or the query failed) must leave the door
            // open for a later render to retry once conditions improve --
            // latching in either case would permanently skip the only prune
            // this scene gets outside a full sync.
            if (currentFileInstanceIds !== undefined) {
                minimapPruneAttemptedForSceneDirName.current = sceneDirName;
            }
        })().catch(() => {
            // Best-effort: not worth reporting -- the full-sync phase and the next
            // scene load both get another chance at pruning the same stale trees.
        });

        return () => abortController.abort();
    }, [
        offlineWorkerState,
        sceneKey,
        sceneDirName,
        minimapTilingEnabled,
        isProcoreDesign,
        viewerSceneId,
        getMinimapCurrentFileInstanceIds,
    ]);

    useEffect(() => {
        handleAction();

        async function handleAction() {
            if (!view || !offlineWorkerState || !action) {
                return;
            }

            let resetAction = true;

            switch (action.action) {
                case "delete": {
                    abortDeleteStaleFiles();
                    const id = action.id ?? sceneDirName;

                    // The delete reports its own status: the SDK announces completion on the
                    // context logger, which carries no scene id and which the viewer doesn't
                    // install, and the scene's own logger is gone by the time it finishes.
                    emitDownloadStatus("delete", { status: "deleting" });
                    try {
                        // When using offline bim-index - it may hold the files open preventing the scene folder to be deleted,
                        // so we need to replace the data access with an online version before deleting the scene.
                        // Only for the open scene: the backend holds nothing open on any other
                        // record the widget lists, and closing the index is process-wide.
                        if (id === sceneDirName) {
                            await replaceDataAccessWithoutOffline();
                        }

                        // By directory name rather than by key: the action can name any record
                        // the UI lists, and those come back from storage as the name their files
                        // are under — including the ones an older keying wrote, whose identity
                        // is no longer recoverable.
                        await offlineWorkerState.getSceneByDirName(id)?.delete();
                    } catch (e) {
                        // Swallowed for the same reason as the fullSync branch below: a rejection
                        // here escapes a fire-and-forget call, skipping the `action` reset and
                        // leaving the UI stuck mid-delete. The scene stays in the store and in
                        // storage, because its files may still be on disk.
                        telemetry.trackCaughtException({
                            error: e,
                            source: "offline.useHandleOffline.delete",
                            severity: "error",
                            category: "data",
                        });
                        // No `error` message: the viewer's copy is all download-flavoured, and
                        // wrong copy is worse for a host than none.
                        emitDownloadStatus("delete", { status: "error", errorCode: "unknown" });
                        break;
                    }
                    storage.remove(id);
                    dispatch(offlineActions.removeScene(id));
                    emitDownloadStatus("delete", { status: "deleted" });

                    // Re-arm incremental caching: addScene broadcasts "add-scene"
                    // which clears the SDK's offlineDirs null-cache for this id.
                    if (isProcoreDesign && !incrementalCachingDisabled && id === sceneDirName) {
                        resetAction = false;
                        dispatch(offlineActions.setAction({ action: "incrementalSync" }));
                    }

                    break;
                }
                case "pause": {
                    abort();
                    break;
                }
                case "incrementalSync": {
                    // Kill switch enforcement point. Only ever true on Procore iOS,
                    // so standalone opt-in (IncrementalLoadingPopup / offline widget)
                    // is unaffected.
                    if (incrementalCachingDisabled) {
                        break;
                    }

                    const scene =
                        offlineWorkerState.getScene(sceneKey) ?? (await offlineWorkerState.addScene(sceneKey));

                    if (!scene) {
                        break;
                    }

                    if (
                        (await scene.readManifest(getSceneIndexUrl(view), abortController.current.signal)) === undefined
                    ) {
                        dispatch(
                            offlineActions.addScene({
                                id: scene.id,
                                name: scene.id,
                                status: "invalid format",
                                lastSync: new Date().toISOString(),
                                progress: "",
                                scanProgress: "",
                                size: 0,
                                viewerScenes: [],
                            }),
                        );
                        break;
                    }

                    let sceneTitle = "";

                    if (isProcoreBackendProvider) {
                        sceneTitle =
                            (await getProcoreModelTitle(viewerSceneId).catch((err) => {
                                console.error(err);
                                return;
                            })) ?? viewerSceneId;
                    } else {
                        const sceneData = await dataApi.loadScene(viewerSceneId);
                        if ("error" in sceneData) {
                            scene.logger?.error({ message: "Failed to load scene data" });
                            break;
                        }
                        sceneTitle = sceneData.title;

                        await Promise.all([
                            warmBookmarks({ projectId: viewerSceneId, signal: abortController.current.signal }),
                            user
                                ? warmBookmarks({
                                      projectId: viewerSceneId,
                                      personal: true,
                                      signal: abortController.current.signal,
                                  })
                                : Promise.resolve(),
                            ...sceneData.objectGroups
                                .filter((group) => group.id && !group.ids)
                                .map((group) => getGroupIds({ projectId: viewerSceneId, groupId: group.id }).unwrap()),
                        ]);
                    }

                    const persisted = await navigator.storage.persisted();
                    if (!persisted) {
                        await navigator.storage.persist();
                    }

                    const meta = storage.get(scene.id);
                    const viewerScene = {
                        id: scene.id,
                        name: sceneTitle,
                        lastSynced: new Date().toISOString(),
                    };

                    const toStore = {
                        id: scene.id,
                        name: scene.id,
                        lastSynced: meta?.lastSynced ?? "",
                        viewerScenes: meta
                            ? meta.viewerScenes.find((vs) => vs.id === scene.id)
                                ? meta.viewerScenes.map((vs) => (vs.id === scene.id ? viewerScene : vs))
                                : meta.viewerScenes.concat(viewerScene)
                            : [viewerScene],
                    };
                    storage.set(toStore);

                    dispatch(
                        offlineActions.addScene({
                            id: scene.id,
                            name: scene.id,
                            status: "incremental",
                            lastSync: toStore.lastSynced,
                            progress: "",
                            scanProgress: "",
                            size: scene.manifest.totalByteSize,
                            viewerScenes: toStore.viewerScenes,
                        }),
                    );

                    scene.logger = createLogger(scene, "incremental");

                    // Best-effort (BM-13752): this scene is being incrementally
                    // armed for the first time this session -- prune any stale
                    // `minimap/{fid}` trees already on disk from before this
                    // scene existed in memory (e.g. a previous full sync of a
                    // scene since re-mapped or edited).
                    if (minimapTilingEnabled) {
                        try {
                            const currentFileInstanceIds = await getMinimapCurrentFileInstanceIds(viewerSceneId);
                            await pruneStaleMinimapFiles({
                                scene: scene as MinimapPruneScene,
                                currentFileInstanceIds,
                                abortSignal: abortController.current.signal,
                            });
                        } catch {
                            // Best-effort: not worth reporting -- the full-sync phase and
                            // the next scene load both get another chance at this.
                        }
                    }
                    break;
                }
                case "readSize": {
                    const scene =
                        offlineWorkerState.getScene(sceneKey) ?? (await offlineWorkerState.addScene(sceneKey));

                    if (!scene) {
                        break;
                    }
                    const size = await scene.getUsedSize();
                    dispatch(
                        offlineActions.updateScene({
                            id: scene.id,
                            updates: {
                                size,
                            },
                        }),
                    );
                    break;
                }
                case "estimate": {
                    const scene =
                        offlineWorkerState.getScene(sceneKey) ?? (await offlineWorkerState.addScene(sceneKey));

                    if (!scene) {
                        break;
                    }

                    if (
                        offlineWorkerState.initialStorageEstimate?.quota === undefined ||
                        offlineWorkerState.initialStorageEstimate?.usage === undefined
                    ) {
                        break;
                    }

                    const availableSize = Math.max(
                        0,
                        offlineWorkerState.initialStorageEstimate.quota -
                            offlineWorkerState.initialStorageEstimate.usage,
                    );
                    const totalSize = await scene.readManifest(
                        view.offline!.manifestUrl,
                        abortController.current.signal,
                    );
                    const usedSize = await scene.getUsedSize();

                    if (totalSize === undefined) {
                        break;
                    }

                    const showWarning = totalSize - usedSize >= availableSize;
                    if (showWarning) {
                        dispatch(
                            offlineActions.setSizeWarning({
                                totalSize,
                                usedSize,
                                availableSize,
                            }),
                        );
                    } else {
                        resetAction = false;
                        dispatch(offlineActions.setAction({ action: "fullSync" }));
                    }

                    break;
                }
                case "fullSync": {
                    const scene =
                        offlineWorkerState.getScene(sceneKey) ?? (await offlineWorkerState.addScene(sceneKey));

                    if (!scene) {
                        break;
                    }

                    // Installed before the checks below so their failures are reported as a full
                    // sync. Otherwise they go to whatever logger the scene already carries — an
                    // incremental one, which reports nothing to the host, or none at all.
                    scene.logger = createLogger(scene, "fullSync");

                    // Procore branch: readManifest + getProcoreModelTitle.
                    // Standalone keeps the dataApi.loadScene + bookmarks path
                    // (calling those for Procore scenes errors and aborts sync).
                    let sceneTitle: string;

                    if (isProcoreBackendProvider) {
                        if (
                            (await scene.readManifest(getSceneIndexUrl(view), abortController.current.signal)) ===
                            undefined
                        ) {
                            scene.logger?.error({ message: "Failed to read the offline manifest" });
                            break;
                        }
                        sceneTitle =
                            (await getProcoreModelTitle(viewerSceneId).catch((err) => {
                                console.error(err);
                                return;
                            })) ?? viewerSceneId;
                    } else {
                        const sceneData = await dataApi.loadScene(viewerSceneId);
                        if ("error" in sceneData) {
                            scene.logger?.error({ message: "Failed to load scene data" });
                            break;
                        }
                        sceneTitle = sceneData.title;

                        await Promise.all([
                            warmBookmarks({ projectId: viewerSceneId, signal: abortController.current.signal }),
                            user
                                ? warmBookmarks({
                                      projectId: viewerSceneId,
                                      personal: true,
                                      signal: abortController.current.signal,
                                  })
                                : Promise.resolve(),
                            ...sceneData.objectGroups
                                .filter((group) => group.id && !group.ids)
                                .map((group) => getGroupIds({ projectId: viewerSceneId, groupId: group.id })),
                        ]);
                    }

                    const persisted = await navigator.storage.persisted();
                    if (!persisted) {
                        await navigator.storage.persist();
                    }

                    const meta = storage.get(scene.id);
                    const viewerScene = {
                        id: scene.id,
                        name: sceneTitle,
                        lastSynced: new Date().toISOString(),
                    };

                    const toStore = {
                        id: scene.id,
                        name: scene.id,
                        lastSynced: meta?.lastSynced ?? "",
                        viewerScenes: meta
                            ? meta.viewerScenes.find((vs) => vs.id === scene.id)
                                ? meta.viewerScenes.map((vs) => (vs.id === scene.id ? viewerScene : vs))
                                : meta.viewerScenes.concat(viewerScene)
                            : [viewerScene],
                    };
                    storage.set(toStore);

                    dispatch(
                        offlineActions.addScene({
                            id: scene.id,
                            name: scene.id,
                            status: "incremental",
                            lastSync: toStore.lastSynced,
                            progress: "",
                            scanProgress: "",
                            size: scene.manifest.totalByteSize,
                            viewerScenes: toStore.viewerScenes,
                        }),
                    );

                    const sync = scene.sync(getSceneIndexUrl(view), abortController.current.signal);
                    // Minimap full sync (BM-13750) is explicitly, permanently mobile-only --
                    // matching the pre-existing enableFullSync gate on the Settings download
                    // button -- independent of whatever the tiled route's own platform scope
                    // becomes (contrast the incremental-caching registry above, which is
                    // deliberately platform-neutral).
                    const attemptMinimapSync = isMobileDesign && minimapTilingEnabled;
                    if (attemptMinimapSync) {
                        minimapPhaseActiveRef.current = true;
                    }

                    if (isProcoreDesign) {
                        // Await so `action` stays set throughout the sync; otherwise
                        // the Download button flickers between SDK status phases.
                        let geometrySyncSucceeded = false;
                        try {
                            await sync;
                            geometrySyncSucceeded = true;
                        } catch (e) {
                            // A rejected sync would otherwise skip the `action` reset
                            // below, leaving the download progress spinner stuck.
                            // Swallow it so `action` clears; surface non-abort failures
                            // via the logger so the spinner is replaced by the error
                            // banner (aborts are user-initiated pauses, not errors).
                            if (!abortController.current.signal.aborted) {
                                scene.logger?.error({ message: e instanceof Error ? e.message : String(e) });
                            }
                        }

                        if (attemptMinimapSync) {
                            if (geometrySyncSucceeded) {
                                // "synchronized" was intercepted (minimapPhaseActiveRef):
                                // this phase runs before the sync reports done, same
                                // ordering the SDK itself uses for its bim-index phase.
                                //
                                // Capture the signal up front: `useAbortController`'s
                                // `abort()` (what "pause" calls) both aborts the current
                                // controller AND immediately replaces `abortController
                                // .current` with a fresh, non-aborted one. Checking
                                // `abortController.current.signal.aborted` in the catch
                                // below would therefore always read `false` after a
                                // pause -- this phase is the only thing that reports the
                                // pause (geometry already finished), so getting that
                                // check wrong means the host silently gets "synchronized"
                                // instead of "aborted".
                                const minimapSyncSignal = abortController.current.signal;
                                try {
                                    const syncedFileInstanceIds = await syncMinimapFilesForScene(
                                        viewerSceneId,
                                        scene,
                                        minimapSyncSignal,
                                        (current, total) => {
                                            if (total > 0) {
                                                dispatch(
                                                    offlineActions.updateScene({
                                                        id: scene.id,
                                                        updates: { progress: ((current / total) * 100).toFixed(2) },
                                                    }),
                                                );
                                            }
                                        },
                                    );

                                    // Best-effort (BM-13752): after the download pass,
                                    // before reporting done, prune any minimap/{fid}
                                    // trees for drawings no longer in this scene (a
                                    // re-mapped or removed sheet). Re-fetches the current
                                    // drawing list rather than reusing `syncedFileInstanceIds`
                                    // outright, to catch a remap landing mid-download -- but
                                    // falls back to it when that re-fetch itself fails, so an
                                    // unrelated transient flap right after a successful sync
                                    // can't silently skip pruning what the sync already knew
                                    // was current.
                                    //
                                    // Never lets a failure (or a pause landing in this
                                    // specific window) hold up `completeFullSync` below --
                                    // unlike the download's own pause handling in the `catch`
                                    // below, a pause that lands here is deliberately treated
                                    // as "close enough to done" and still reports
                                    // `synchronized`, since the download itself (the part a
                                    // pause is actually meant to interrupt) already finished.
                                    try {
                                        const currentFileInstanceIds =
                                            (await getMinimapCurrentFileInstanceIds(viewerSceneId)) ??
                                            syncedFileInstanceIds;
                                        await pruneStaleMinimapFiles({
                                            scene: scene as MinimapPruneScene,
                                            currentFileInstanceIds,
                                            abortSignal: minimapSyncSignal,
                                        });
                                    } catch {
                                        // Best-effort: not worth reporting -- the next full
                                        // sync or scene load gets another chance at this.
                                    }

                                    minimapPhaseActiveRef.current = false;
                                    completeFullSync(scene);
                                } catch (e) {
                                    minimapPhaseActiveRef.current = false;
                                    if (minimapSyncSignal.aborted) {
                                        // The geometry sync already finished, so nothing
                                        // else will report the pause -- do it here.
                                        scene.logger?.status("aborted");
                                    } else {
                                        // Best-effort: a minimap failure never fails the
                                        // sync as a whole. Re-pressing Download retries
                                        // cheaply thanks to the journal-union dedupe.
                                        telemetry.trackCaughtException({
                                            error: e,
                                            source: "offline.useHandleOffline.syncMinimapFiles",
                                            category: "data",
                                            severity: "warning",
                                        });
                                        completeFullSync(scene);
                                    }
                                }
                            } else {
                                // The geometry sync itself failed/aborted; its own
                                // status callback (error/aborted) already fired,
                                // un-intercepted (only "synchronized" is), so there is
                                // nothing further to report.
                                minimapPhaseActiveRef.current = false;
                            }
                        }
                    }
                    // No `else` branch for standalone: `attemptMinimapSync` requires
                    // `isMobileDesign`, which implies `isProcoreDesign` -- standalone
                    // never sets `minimapPhaseActiveRef` in the first place.

                    break;
                }
            }

            if (resetAction) {
                dispatch(offlineActions.setAction(undefined));
            }
        }
    }, [
        action,
        dispatch,
        offlineWorkerState,
        view,
        abort,
        abortController,
        abortDeleteStaleFiles,
        createLogger,
        viewerSceneId,
        sceneKey,
        sceneDirName,
        user,
        getGroupIds,
        warmBookmarks,
        projectId,
        getProcoreModelTitle,
        isProcoreBackendProvider,
        isProcoreDesign,
        isMobileDesign,
        minimapTilingEnabled,
        syncMinimapFilesForScene,
        getMinimapCurrentFileInstanceIds,
        completeFullSync,
        minimapPhaseActiveRef,
        incrementalCachingDisabled,
        emitDownloadStatus,
        replaceDataAccessWithoutOffline,
    ]);
}

/**
 * Fills the HTTP cache with a scope's bookmarks so they can be applied while offline.
 *
 * Best-effort: bookmarks are auxiliary to the scene data, so a failure is reported rather
 * than allowed to abort the sync, and pausing the sync stops the warm without reporting
 * anything. A scope can hold hundreds of bookmarks and every state is a full payload, so the
 * fetches are throttled instead of issued all at once.
 *
 * The states go out as plain fetches rather than through RTK Query: only the service worker's
 * copy is wanted, and RTK Query would keep every payload in the store until its cache expires.
 */
export function useWarmBookmarks() {
    const [getBookmarkCatalog] = useLazyGetBookmarkCatalogQuery();
    const store = useAppStore();

    return useCallback(
        async ({
            projectId,
            personal = false,
            signal,
        }: {
            projectId: string;
            personal?: boolean;
            signal: AbortSignal;
        }) => {
            try {
                const catalog = await getBookmarkCatalog({ projectId, personal }).unwrap();

                await pMap(
                    catalog,
                    async (entry) => {
                        // Read per bookmark rather than per scope so a token refreshed
                        // mid-warm is picked up.
                        const rootState = store.getState();
                        const headers = dataV2Headers(rootState);
                        // The base query asks for json on every request, and the cached response
                        // only answers a later request carrying the same headers.
                        headers.set("accept", "application/json");

                        const response = await fetch(
                            selectConfig(rootState).dataV2ServerUrl +
                                bookmarkStatePath({ projectId, bookmarkId: entry.id, personal }),
                            { headers, signal },
                        );

                        if (!response.ok) {
                            throw new Error(`Bookmark ${entry.id} responded ${response.status}`);
                        }

                        // The payload is only wanted in the cache, but the service worker cannot
                        // store it until the body has been read.
                        await response.arrayBuffer();
                    },
                    { concurrency: MAX_BOOKMARK_WARM_CONCURRENCY, signal },
                );
            } catch (error) {
                if (signal.aborted) {
                    return;
                }

                console.warn(error);
                telemetry.trackCaughtException({
                    source: "offline.useHandleOffline.warmBookmarks",
                    category: "data",
                    error,
                });
            }
        },
        [getBookmarkCatalog, store],
    );
}

/**
 * Let go of the session's object data access and publish a replacement chosen for a scene
 * that no longer has offline files — for the delete action, which is the one thing that
 * takes those files away.
 *
 * The backend is otherwise picked once at startup and never swapped underneath a consumer
 * (see `useHandleInit`). Here the pick is redone with no offline state at all, which is
 * what makes it skip the offline index and land on the served one or the object DB.
 *
 * Disposing is what closes the BIM index worker, and so what releases the OPFS locks it
 * holds on the deleted directory. It happens before the delete for that reason, though it
 * is not a guarantee: `closeBimIndex` terminates the worker without waiting for its
 * `close` to land, so the files may still be locked when the delete reaches them. The
 * SDK's delete copes with files it cannot remove yet.
 *
 * It happens *after* the replacement is published, though, and only if there is one:
 * closing the index is process-wide, so a disposed backend left on globals is a session
 * whose search and tree cannot answer until the next scene load. A pick that fails
 * therefore leaves the offline backend in place and the files locked — the delete then
 * likely fails too, which is the outcome that keeps the session usable.
 *
 * Only the open scene's files are ever held, so only its delete needs this — the caller
 * decides, since closing the index is process-wide and would cost any other session too.
 *
 * The current backend is read through a ref so the callback keeps its identity when the
 * replacement is published: it is a dependency of the effect that calls it, and a fresh
 * identity would re-run the delete it was in the middle of.
 */
function useReplaceDataAccessWithoutOffline() {
    const {
        state: { db, view, dataAccess },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const procoreContext = useAppSelector(selectProcoreContext);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    const dataAccessRef = useRef(dataAccess);
    dataAccessRef.current = dataAccess;

    return useCallback(async () => {
        const previous = dataAccessRef.current;

        if (!db || !view) {
            // Nothing to pick a replacement from, and so nothing that could still be asking
            // the backend anything. Let the index go rather than leave the files locked.
            previous?.dispose();
            return;
        }

        const sceneVersionId = procoreContext?.sceneVersionId;

        try {
            const dataAccess = await pickDataAccess(
                db,
                view,
                undefined,
                procoreContext?.enableBimIndex ?? false,
                isProcoreBackendProvider && procoreContext && sceneVersionId
                    ? {
                          companyId: procoreContext.companyId,
                          projectId: procoreContext.projectId,
                          sceneId,
                          sceneVersionId,
                          sceneSliceId: procoreContext.sceneSliceId,
                      }
                    : undefined,
            );
            dispatchGlobals(explorerGlobalsActions.update({ dataAccess }));
            previous?.dispose();
        } catch (e) {
            // Swallowed for the reason the delete swallows its own failures: this is awaited
            // inside a fire-and-forget action handler, and a rejection here would skip the
            // `action` reset and leave the UI stuck mid-delete. The published backend stays
            // as it was — still the offline one, and still able to answer — so the session
            // survives a delete that could not go through.
            telemetry.trackCaughtException({
                error: e,
                source: "offline.useHandleOffline.replaceDataAccess",
                severity: "error",
                category: "data",
            });
        }
    }, [db, view, procoreContext, isProcoreBackendProvider, sceneId, dispatchGlobals]);
}

function useCreateLogger() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const emitDownloadStatus = useEmitDownloadStatus();
    // Read via a ref so the logger can tell whether an error happened during a
    // user-initiated full sync without recreating the memoized logger (which is
    // stored on scene.logger) whenever the action changes. For Procore the
    // fullSync action stays set for the whole sync (it's awaited).
    const action = useAppSelector(selectOfflineAction);
    const actionRef = useRef(action);
    actionRef.current = action;

    // Set while the minimap full-sync phase (BM-13750) is running, after the
    // SDK's own geometry sync already reported `"synchronized"`. A single
    // ref, not keyed by scene, matches the rest of this file's assumption
    // that only one full sync is ever in flight at a time.
    const minimapPhaseActiveRef = useRef(false);

    /**
     * The `"synchronized"` handling of the logger below, factored out so the minimap phase
     * (BM-13750) can defer into it after the SDK's own sync has already reached that status.
     * @param scene The synced scene: its {@link OfflineScene.id} names the record, its manifest
     *   is what the synced size is read from -- same as the logger it was lifted from.
     * @param reportToHost Whether to emit the status to the host. `true` for a full sync (the
     *   minimap phase always is one); the incremental logger passes `false`.
     */
    const completeFullSync = useCallback(
        (scene: OfflineScene, reportToHost = true) => {
            if (reportToHost) {
                emitDownloadStatus("fullSync", { status: "synchronized" });
            }
            const now = new Date().toISOString();
            const meta = storage.get(scene.id);
            if (meta) {
                storage.set({ ...meta, lastSynced: now });
            }
            dispatch(
                offlineActions.updateScene({
                    id: scene.id,
                    updates: {
                        status: "synchronized",
                        lastSync: now,
                        size: scene.manifest.totalByteSize,
                    },
                }),
            );
        },
        [dispatch, emitDownloadStatus],
    );

    /**
     * @param scene The scene the logger is installed on. Its {@link OfflineScene.id} names the
     *   record every status goes to, and its manifest is what the synced size is read from.
     * @param source Which offline work the logger reports on. Only `"fullSync"` is reported to the
     *   host; incremental caching stays in the viewer.
     */
    const createLogger = useCallback(
        (scene: OfflineScene, source: "fullSync" | "incremental"): OfflineViewState["logger"] => {
            const reportToHost = source === "fullSync";
            const id = scene.id;

            return {
                status: (status) => {
                    if (status === "synchronized") {
                        // Defer: the minimap phase (BM-13750) still has to run after
                        // the SDK's own sync reaches this point, so hold off on both
                        // the host emit and the redux/storage update -- the phase
                        // calls completeFullSync itself once it resolves. Not gated
                        // on `reportToHost` because incremental syncs never set this ref.
                        if (minimapPhaseActiveRef.current) {
                            return;
                        }
                        completeFullSync(scene, reportToHost);
                        return;
                    }

                    if (reportToHost && isHostReportedStatus(status)) {
                        emitDownloadStatus("fullSync", { status });
                    }
                    dispatch(offlineActions.updateScene({ id, updates: { status } }));
                },
                error: (error) => {
                    telemetry.trackCaughtException({
                        error,
                        source: "offline.useHandleOffline.useCreateLogger",
                        severity: "warning",
                        category: "data",
                    });

                    const msg = getOfflineDownloadErrorMessage(error.id, t);

                    if (reportToHost) {
                        emitDownloadStatus("fullSync", {
                            status: "error",
                            error: msg,
                            errorCode: getOfflineDownloadErrorCode(error.id),
                        });
                    }

                    dispatch(
                        offlineActions.updateScene({
                            id,
                            updates: {
                                status: "error",
                                error: msg,
                                errorFromFullSync: actionRef.current?.action === "fullSync",
                            },
                        }),
                    );
                },
                progress: (current, max, operation) => {
                    if (operation === "download") {
                        if (current === max) {
                            dispatch(
                                offlineActions.updateScene({
                                    id,
                                    updates: { progress: "100%", size: max },
                                }),
                            );
                        }

                        if (!max) {
                            dispatch(offlineActions.updateScene({ id, updates: { progress: "" } }));
                        }

                        if (current !== undefined && max !== undefined) {
                            const progress = ((current / max) * 100).toFixed(2);
                            dispatch(offlineActions.updateScene({ id, updates: { progress, size: max } }));
                        }
                    } else {
                        if (current === max) {
                            dispatch(offlineActions.updateScene({ id, updates: { scanProgress: "100%" } }));
                        }

                        if (!max) {
                            dispatch(
                                offlineActions.updateScene({
                                    id,
                                    updates: { scanProgress: String(current) },
                                }),
                            );
                        }

                        if (current !== undefined && max !== undefined) {
                            const scanProgress = ((current / max) * 100).toFixed(2);
                            dispatch(offlineActions.updateScene({ id, updates: { scanProgress } }));
                        }
                    }
                },
            };
        },
        [dispatch, t, emitDownloadStatus, completeFullSync],
    );

    return { createLogger, completeFullSync, minimapPhaseActiveRef };
}

/** What one status change has to report; the emitter stamps the identity fields. */
type OfflineDownloadStatusUpdate = Omit<
    OfflineDownloadStatusPayload,
    "sceneId" | "sceneVersionId" | "sceneSliceId" | "source"
>;

/**
 * Reports offline status changes to the host embedding the viewer, via the internal emitter the
 * Webviewer bridge forwards to `webviewer.eventEmitter`.
 *
 * Only the SDK's status and error callbacks are forwarded; the download progress callback is left to
 * the in-viewer indicator. Incremental caching reaches those callbacks through the same kind of
 * logger, so callers gate on the logger's `source` rather than reporting everything they hear.
 *
 * The open scene's ids are stamped here so every event carries the same identity, whichever callback
 * it came from.
 */
function useEmitDownloadStatus() {
    const {
        state: { internalEventEmitter },
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneVersionId = procoreContext?.sceneVersionId;
    const sceneSliceId = procoreContext?.sceneSliceId;

    return useCallback(
        (source: OfflineDownloadStatusPayload["source"], update: OfflineDownloadStatusUpdate) => {
            void internalEventEmitter?.emit("offlineDownloadStatus", {
                ...update,
                sceneId,
                ...(sceneVersionId ? { sceneVersionId } : {}),
                ...(sceneSliceId ? { sceneSliceId } : {}),
                source,
            });
        },
        [internalEventEmitter, sceneId, sceneVersionId, sceneSliceId],
    );
}

function getSceneIndexUrl(view: View): URL {
    const url = view.renderState.scene?.url ? new URL(view.renderState.scene.url) : undefined;

    if (!url) {
        throw new Error("Unable to parse url for SAS key");
    }

    // Check if this is a Procore FAS URL (contains /versions/)
    // Procore FAS requires ?file= query parameter instead of pathname concatenation
    if (url.pathname.includes("/versions/")) {
        url.searchParams.set("file", "index.json");
    } else {
        // Novorender-style: append to pathname
        url.pathname += "index.json";
    }

    return url;
}

function useGetProcoreModelTitle() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getModels] = useLazyGetModelsQuery();

    return useCallback(
        async (sceneId: string) => {
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }

            // preferCacheValue: reuse the shared session cache instead of refetching.
            const [modelRevisionsData, modelsData] = await Promise.all([
                getModelRevisions(revisionsArgs, true).unwrap(),
                getModels(
                    {
                        companyId: procoreContext.companyId,
                        projectId: procoreContext.projectId,
                    },
                    true,
                ).unwrap(),
            ]);

            const modelRevision = modelRevisionsData?.[0];
            const model = modelRevision ? modelsData?.data.find((m) => m.id === modelRevision.bim_model_id) : undefined;

            return model?.name;
        },
        [procoreContext, getModelRevisions, getModels],
    );
}

/**
 * Enumerates the open model's drawings via the mini map data (works even if
 * the mini map UI was never opened, since it goes through the same lazy
 * query the UI uses) and downloads all of their tile sets into OPFS
 * (BM-13750, RFD00000145).
 *
 * Deliberately always refetches (no `preferCacheValue`, unlike
 * `useGetProcoreModelTitle`): a full sync's whole point is to persist
 * *current* tile sets, so it must not reuse a session cache that predates a
 * re-mapping or a newly added sheet.
 *
 * Returns the `platformFileInstanceId`s it just used to download, or
 * `undefined` when it bailed before determining them (missing Procore
 * context, or no resolvable revision) -- so a caller doing its own follow-up
 * work (BM-13752's prune) can fall back to a set it already knows is current,
 * rather than needing its own independent, independently-fallible re-fetch.
 */
function useSyncMinimapFiles() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getMiniMapData] = useLazyGetMiniMapDataQuery();

    return useCallback(
        async (
            sceneId: string,
            scene: MinimapFullSyncScene,
            abortSignal: AbortSignal,
            onProgress?: (current: number, total: number) => void,
        ): Promise<string[] | undefined> => {
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }

            // Deliberately NOT `preferCacheValue`, unlike `useGetProcoreModelTitle`:
            // a full sync's whole point is to persist *current* tile sets, so it
            // must not reuse a session cache that predates a re-mapping or a newly
            // added sheet -- the opposite of what a user pressing "Download for
            // offline" expects. Also picks the revision matching the opened scene
            // (`selectSceneRevision`) rather than `[0]`, since a full sync is a
            // worse place than the mini map UI to be silently on the wrong one.
            const modelRevisionsData = await getModelRevisions(revisionsArgs).unwrap();
            const modelRevisionId = selectSceneRevision(modelRevisionsData, {
                sceneVersionId: procoreContext.sceneVersionId,
                sceneId,
            })?.id;
            if (!modelRevisionId) {
                return undefined;
            }

            const payload = await getMiniMapData({
                companyId: procoreContext.companyId,
                projectId: procoreContext.projectId,
                modelRevisionId,
            }).unwrap();

            const fileInstanceIds = new Set<string>();
            for (const drawing of Object.values(payload.drawings)) {
                if (drawing.drawingData.platformFileInstanceId) {
                    fileInstanceIds.add(drawing.drawingData.platformFileInstanceId);
                }
            }

            await syncMinimapFiles({
                scene,
                fileInstanceIds: [...fileInstanceIds],
                resolveBaseUrl: (fileInstanceId, options) =>
                    createBimSceneFileSignedDownloadUrl(
                        {
                            companyId: procoreContext.companyId,
                            projectId: procoreContext.projectId,
                            sceneId,
                            sceneVersionId: procoreContext.sceneVersionId ?? "current",
                            fileInstanceId,
                        },
                        options,
                    ),
                abortSignal,
                onProgress,
            });

            return [...fileInstanceIds];
        },
        [procoreContext, getModelRevisions, getMiniMapData],
    );
}

/**
 * The open model's current `platformFileInstanceId`s, for {@link pruneStaleMinimapFiles}
 * (BM-13752), or `undefined` when they couldn't be determined -- offline, or
 * the drawing-list query failed. Deliberately never throws: the prune must
 * treat "don't know" as "prune nothing", not let a transient API error
 * propagate and risk being read as "every drawing is gone".
 *
 * Same fields and the same deliberately-uncached refetch as
 * `useSyncMinimapFiles`'s own enumeration, but kept separate rather than
 * shared: that hook needs a failure to propagate (its caller already treats
 * a minimap sync failure as best-effort and telemetries it there), while this
 * one needs failures caught internally instead.
 */
function useMinimapCurrentFileInstanceIds() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getMiniMapData] = useLazyGetMiniMapDataQuery();

    return useCallback(
        async (sceneId: string): Promise<string[] | undefined> => {
            if (!navigator.onLine) {
                return undefined;
            }
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }
            try {
                const modelRevisionsData = await getModelRevisions(revisionsArgs).unwrap();
                const modelRevisionId = selectSceneRevision(modelRevisionsData, {
                    sceneVersionId: procoreContext.sceneVersionId,
                    sceneId,
                })?.id;
                if (!modelRevisionId) {
                    return undefined;
                }

                const payload = await getMiniMapData({
                    companyId: procoreContext.companyId,
                    projectId: procoreContext.projectId,
                    modelRevisionId,
                }).unwrap();

                const fileInstanceIds = new Set<string>();
                for (const drawing of Object.values(payload.drawings)) {
                    if (drawing.drawingData.platformFileInstanceId) {
                        fileInstanceIds.add(drawing.drawingData.platformFileInstanceId);
                    }
                }
                return [...fileInstanceIds];
            } catch {
                // Not worth reporting -- the prune this feeds just skips this round.
                return undefined;
            }
        },
        [procoreContext, getModelRevisions, getMiniMapData],
    );
}
